import Link from 'next/link';
import { useReduxState } from 'hooks';
import { Icon } from 'components/common/icons';

import type { JSX, Ref } from 'react';
import type { IconProp } from 'components/common/icons/Icon';

export type ButtonSize =
  | 'tiny'
  | 'small'
  | 'medium'
  | 'header'
  | 'large'
  | 'huge';

export type ButtonCategory =
  | 'danger'
  | 'default'
  | 'group'
  | 'light'
  | 'lighter'
  | 'link'
  | 'link-with-background'
  | 'success'
  | 'transparent'
  | 'white';

export interface ButtonInterface
  extends React.ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
  active?: boolean;
  category?: ButtonCategory;
  className?: string;
  customIcon?: string;
  customBackgroundColor?: string;
  customBackgroundColorHover?: string;
  customColor?: string;
  darkMode?: boolean;
  disabled?: boolean;
  href?: string;
  icon?: IconProp;
  iconStyle?: React.CSSProperties;
  isGroupActive?: boolean;
  isRounded?: boolean;
  isSemiRounded?: boolean;
  loading?: boolean;
  onClick?: (
    event?: React.MouseEvent | React.SyntheticEvent,
    ...params
  ) => void;
  ref?: Ref<HTMLButtonElement> | Ref<HTMLAnchorElement>;
  size?: ButtonSize;
  style?: React.CSSProperties;
  tagName?: 'button' | 'a';
  text?: string | JSX.Element;
  textBadge?: string;
  textHideBreakpoint?: number;
  tooltip?: string;
  type?: 'button' | 'submit' | 'reset';
  underlineOnHover?: boolean;
  unreadItems?: number;
  warningItems?: number;
}

const Button = ({
  active,
  category,
  className,
  customIcon,
  customBackgroundColor,
  customBackgroundColorHover,
  customColor,
  darkMode,
  disabled,
  href,
  icon,
  iconStyle,
  isGroupActive,
  isRounded,
  isSemiRounded,
  loading,
  onClick,
  ref,
  size,
  style,
  tagName = 'button',
  text,
  textBadge,
  textHideBreakpoint,
  tooltip,
  type,
  underlineOnHover = false,
  unreadItems,
  warningItems,
  ...params
}: ButtonInterface) => {
  const { theme } = useReduxState();

  const ButtonElement = href ? Link : 'button';

  const loadingContent = loading ? (
    <span className="loading" style={text ? { marginLeft: 8 } : {}}>
      <style jsx>{`
        @keyframes loading {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .loading {
          animation: loading 1.2s infinite linear;
          border-style: solid;
          border-radius: 14px;
          border-top-color: transparent;
          border-width: 2px;
          display: inline-block;
          height: 12px;
          position: relative;
          top: 2px;
          width: 12px;
        }
      `}</style>
    </span>
  ) : null;

  // category
  let color = theme.mainTextColor;
  let colorHover = null;
  let backgroundColor = theme.mainColor;
  let backgroundColorHover = theme.mainColorActive;
  let boxShadow = 'none';
  let boxShadowHover = 'none';

  if (category === 'success') {
    color = darkMode ? theme.successTextColorDark : theme.successTextColor;
    backgroundColor = darkMode ? theme.successColorDark : theme.successColor;
    backgroundColorHover = darkMode
      ? theme.successColorDarkActive
      : theme.successColorActive;
  } else if (category === 'danger') {
    color = theme.dangerTextColor;
    backgroundColor = theme.dangerColor;
    backgroundColorHover = theme.dangerColorActive;
  } else if (category === 'light' || (category === 'group' && isGroupActive)) {
    color = theme.textColorLight;
    backgroundColor = isGroupActive
      ? theme.lightGreyDarkActive
      : theme.lightGreyDark;
    backgroundColorHover = theme.lightGreyDarkActive;
  } else if (
    category === 'lighter' ||
    (category === 'group' && !isGroupActive)
  ) {
    color = theme.textColorLight;
    backgroundColor = theme.lightGrey;
    backgroundColorHover = theme.lightGreyActive;
  } else if (category === 'transparent') {
    color = theme.textColorLighter;
    backgroundColor = 'transparent';
    backgroundColorHover = theme.lightGrey;
  } else if (category === 'white') {
    color = theme.textColorLighter;
    colorHover = theme.textColor;
    backgroundColor = 'white';
    backgroundColorHover = 'white';
    boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
    boxShadowHover = '0 1px 3px 0 rgba(0, 0, 0, 0.2)';
  } else if (category === 'link') {
    color = 'inherit';
    backgroundColor = 'transparent';
    backgroundColorHover = 'transparent';
  } else if (category === 'link-with-background') {
    color = theme.textColorLight;
    backgroundColor = theme.lightGrey;
    backgroundColorHover = theme.lightGrey;
  }

  // size
  let borderRadius = theme.buttonBorderRadius;
  let fontSize = '1.4rem';
  let fontWeight = '700';
  let padding = '8px 14px';
  let fixedPadding = '8px 0';
  let fixedHeight = '34px';
  let fixedWidth = '34px';

  if (size === 'tiny') {
    fontSize = '1.2rem';
    padding = '4px 6px 4px';
    fixedPadding = '4px 0 4px';
    fixedHeight = '26px';
    fixedWidth = '26px';
  } else if (size === 'small') {
    fontSize = '1.2rem';
    padding = '6px 10px 7px';
    fixedPadding = '6px 0 7px';
    fixedHeight = '28px';
    fixedWidth = '28px';
  } else if (size === 'header') {
    fontSize = '1.4rem';
    padding = '6px 10px';
    fixedPadding = '6px 0';
    fixedHeight = '30px';
    fixedWidth = '30px';
  } else if (size === 'large') {
    fontSize = '1.6rem';
    padding = '9px 14px';
    fixedPadding = '9px 0';
    fixedHeight = '38px';
    fixedWidth = '38px';
  } else if (size === 'huge') {
    fontSize = '1.8rem';
    padding = '11px 17px';
    fixedPadding = '11px 0';
    fixedHeight = '45px';
    fixedWidth = '45px';
  }

  if (isSemiRounded) {
    borderRadius = '12px';
  } else if (isRounded) {
    borderRadius = '50px';
  }

  padding = text ? padding : fixedPadding;
  const height = text ? 'auto' : fixedHeight;
  const width = text ? 'auto' : fixedWidth;

  if (category === 'link') {
    padding = '0px';
  } else if (category === 'link-with-background') {
    fontWeight = '600';
  }

  return (
    <ButtonElement
      className={
        'button' +
        (active ? ' active' : '') +
        (boxShadow !== 'none'
          ? ' focus-box-shadow-double'
          : ' focus-box-shadow') +
        (category === 'group' ? ' grouped' : '') +
        (category === 'link' || category === 'link-with-background'
          ? ' button-link'
          : '') +
        (className ? ` ${className}` : '')
      }
      disabled={disabled || loading}
      href={href}
      onClick={onClick}
      // @ts-ignore
      ref={ref}
      type={type || 'button'}
      style={style}
      {...(tooltip
        ? {
            ['aria-label']: tooltip,
            ['data-tooltip']: tooltip
          }
        : {})}
      {...params}
    >
      {(!loading || text) && (icon || customIcon) ? (
        <Icon
          {...(customIcon
            ? { icon: customIcon, type: 'custom' }
            : { icon, type: 'font-awesome' })}
          style={{
            fontSize: {
              tiny: '1rem',
              small: '1.1rem',
              header: '1.2rem',
              medium: '1.2rem',
              large: '1.4rem',
              huge: '1.6rem'
            }[size || 'medium'],
            marginRight: text || loading ? '5px' : '0px',
            position: 'relative',
            top: text
              ? {
                  small: 0,
                  header: 0,
                  medium: 0,
                  large: -1,
                  huge: 0
                }[size || 'medium']
              : 0,
            ...iconStyle
          }}
        />
      ) : null}

      <span className={`text ${textHideBreakpoint ? 'text-hide' : ''}`}>
        {text}
      </span>

      {textBadge ? (
        <div
          className="text-badge"
          style={{
            fontSize: {
              tiny: '1rem',
              small: '1.1rem',
              header: '1.2rem',
              medium: '1.2rem',
              large: '1.4rem',
              huge: '1.6rem'
            }[size || 'medium']
          }}
        >
          {textBadge}
        </div>
      ) : null}

      {unreadItems || warningItems ? (
        <div
          className={unreadItems ? 'unread-items' : 'warning-items'}
          style={{
            fontSize: {
              tiny: '1.1rem',
              small: '1.2rem',
              header: '1.2rem',
              medium: '1.3rem',
              large: '1.4rem',
              huge: '1.6rem'
            }[size || 'medium']
          }}
        >
          {unreadItems || warningItems}
        </div>
      ) : null}

      {loadingContent}

      <style jsx>{`
        .button {
          background-color: ${customBackgroundColor || backgroundColor};
          border: 0;
          border-radius: ${borderRadius};
          box-shadow: ${boxShadow};
          color: ${customColor || color};
          cursor: pointer;
          display: inline-block;
          font-family: inherit;
          font-size: ${fontSize};
          font-weight: ${fontWeight};
          height: ${height};
          margin: 0;
          outline: 0;
          padding: ${padding};
          position: relative;
          text-align: center;
          text-decoration: none;
          transition: background-color 0.3s, box-shadow 0.3s, color 0.3s,
            opacity 0.3s;
          white-space: nowrap;
          width: ${width};
        }
        .button:disabled {
          cursor: default;
          opacity: 0.5;
        }
        .button.active,
        .button:hover {
          background-color: ${customBackgroundColorHover ||
          backgroundColorHover};
          box-shadow: ${boxShadowHover};
          color: ${customColor || colorHover || color};
        }
        .button:disabled:hover {
          background-color: ${backgroundColor};
        }
        .button:focus {
          box-shadow: ${boxShadow !== 'none'
            ? `${boxShadow}, 0 0 0 5px rgba(0, 0, 0, 0.15)`
            : '0 0 0 5px rgba(0, 0, 0, 0.15)'};
        }
        .button.grouped {
          border-radius: 0;
        }
        .button.grouped:first-child {
          border-radius: ${`${borderRadius} 0 0 ${borderRadius}`};
        }
        .button.grouped:last-child {
          border-radius: ${`0 ${borderRadius} ${borderRadius} 0`};
        }
        .button-link:not(:disabled):focus,
        .button-link:not(:disabled):hover {
          text-decoration: underline;
        }
        .text:hover {
          text-decoration: ${underlineOnHover ? 'underline' : ''};
        }
        .text-badge {
          background-color: ${customColor || color};
          color: ${customBackgroundColor || backgroundColor};
          border-radius: 100px;
          display: inline-block;
          font-weight: 700;
          margin: -1px 0 -1px 10px;
          padding: 1px 6px;
        }
        .unread-items,
        .warning-items {
          border-radius: 20px;
          font-weight: 700;
          line-height: 1;
          padding: 2px 6px 3px;
          position: absolute;
          right: -4px;
          top: -6px;
        }
        .unread-items {
          background-color: ${theme.dangerColor};
          color: ${theme.dangerTextColor};
        }
        .warning-items {
          background-color: ${theme.warningColor};
          color: ${theme.warningTextColor};
        }
        @media (max-width: ${textHideBreakpoint || 0}px) {
          .button {
            padding: ${fixedPadding};
            height: ${fixedWidth};
            width: ${fixedWidth};
          }
          .text-hide {
            display: none;
          }
          .button :global(svg) {
            margin-right: 0 !important;
          }
        }
      `}</style>
    </ButtonElement>
  );
};

export default Button;
